import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from '@unform/web';
import { isUndefined } from 'lodash';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Input, Autocomplete, Select } from '~/components/Form';
import { t, validCharacters } from '~/helpers';
import {
  storeData,
  updateData,
} from '~/store/modules/linhaAbastecimento/actions';

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector(
    (state) => state.linhaAbastecimento
  );
  const [posto, setPosto] = useState(0);
  const [enderecoconversor, setEnderecoconversor] = useState('');

  const initials = create ? data : { ...find, ...data };

  const [varControleVazao, setVarControleVazao] = useState(
    create ? '0' : initials.controlevazaolinhaabastecimento
  );

  const statusControleVazao = [
    { label: t('s.geral.opcoes.nao'), value: '0' },
    { label: t('s.geral.opcoes.sim'), value: '1' },
  ];

  const handleSubmit = useCallback(
    async (param) => {
      try {
        const schema = Yup.object().shape({
          idposto: create
            ? Yup.number().required(t('s.geral.validacao.campoobrigatorio'))
            : Yup.string(),
          numerolinhaabastecimento: Yup.number()
            .required(t('s.geral.validacao.campoobrigatorio'))
            .integer(t('s.geral.validacao.inteiro')) // valor deve ser inteiro
            .min(1, t('s.geral.validacao.valorminimo').replace('%s', 1))
            .max(100, t('s.geral.validacao.valormaximo').replace('%s', 100)),
          descricaolinhaabastecimento: Yup.string()
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .required(t('s.geral.validacao.campoobrigatorio'))
            .max(30, t('s.geral.validacao.tamanhomaximo').replace('%s', 30)),
          controlevazaolinhaabastecimento: Yup.string()
            .typeError(t('s.geral.validacao.campoobrigatorio'))
            .required(t('s.geral.validacao.campoobrigatorio')),
          idinversorfrequencia:
            varControleVazao === '1'
              ? Yup.number()
                  .typeError(t('s.geral.validacao.campoobrigatorio'))
                  .required(t('s.geral.validacao.campoobrigatorio'))
                  .integer(t('s.geral.validacao.inteiro')) // valor deve ser inteiro
                  .min(1, t('s.geral.validacao.valorminimo').replace('%s', 1))
                  .max(
                    255,
                    t('s.geral.validacao.valormaximo').replace('%s', 255)
                  )
              : Yup.string().nullable(),
        });

        await schema.validate(param, {
          abortEarly: false,
        });

        const objSend = {
          ...initials,
          ...param,
          idcliente: client,
          enderecoconversorinversorfrequencia:
            param.enderecoconversorinversorfrequencia || enderecoconversor,
        };

        if (create) {
          dispatch(storeData(objSend, params));
        } else {
          dispatch(updateData(objSend, params));
        }
      } catch (err) {
        const validationErrors = {};

        if (err instanceof Yup.ValidationError) {
          err.inner.forEach((error) => {
            validationErrors[error.path] = error.message.replace(
              `${error.path} `,
              ''
            );
          });

          reference.current.setErrors(validationErrors);
        }
      }
    },
    [create, client, initials]
  );

  useEffect(() => {
    const { idposto, enderecoconversorinversorfrequencia } = initials;

    if (!isUndefined(idposto)) {
      setPosto(idposto);
      setEnderecoconversor(enderecoconversorinversorfrequencia);
    }
  }, [find]);

  async function handleControleVazao(value) {
    await reference.current.setFieldValue(
      'controlevazaolinhaabastecimento',
      value
    );
    await setVarControleVazao(value);

    if (varControleVazao === '0') {
      await setEnderecoconversor('');
    }
  }

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <Autocomplete
            name="idposto"
            label="codigonomeposto"
            url="postoscomboios"
            unique="idposto"
            title={t('s.geral.postocomboio')}
            disabled={!create}
            getValue={({ idposto }) => {
              setPosto(idposto);
            }}
          />
          <Input
            name="numerolinhaabastecimento"
            label={t('s.configuracao.tanquefisico.numerosondatanquefisico')}
            disabled={!create}
          />
          <Input
            name="descricaolinhaabastecimento"
            label={t('s.cadastro.safra.descricaosafra')}
          />
          <Select
            name="controlevazaolinhaabastecimento"
            label={t('s.configuracao.linhaabastecimento.controlevazao')}
            options={statusControleVazao}
            defaultValue={
              create ? '0' : initials.controlevazaolinhaabastecimento
            } // Default sem controle
            onChange={({ target }) => {
              handleControleVazao(target.value);
            }}
          />
          {varControleVazao === '1' && (
            <Autocomplete
              name="idinversorfrequencia"
              label="enderecoidinversorfrequencia"
              url="inversoresfrequencias"
              unique="idinversorfrequencia"
              title={t('s.menu.configuracao.inversoresfrequencias')}
              hiddens={[{ name: 'enderecoconversorinversorfrequencia' }]}
              params={{
                wherefield: 'idposto',
                valuewherefield: initials.idposto || posto,
                typewherefield: 'EQUALS',
              }}
            />
          )}
        </Form>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
